import { useState, useEffect } from 'react';
import { X, Plus, Copy, AlertTriangle } from 'lucide-react';
import { newReleasesApi } from '../../services/adminApi';

const CreateListModal = ({ isOpen, onClose, onSuccess }) => {
  const [mode, setMode] = useState('create'); // 'create' or 'duplicate'
  const [formData, setFormData] = useState({
    month: '',
    source_month: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const currentDate = new Date();
      const defaultMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;

      setFormData({
        month: defaultMonth,
        source_month: ''
      });
      setMode('create');
      setError(null);
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.month) {
      setError('Month is required.');
      return;
    }

    if (mode === 'duplicate' && !formData.source_month) {
      setError('Source month is required for duplication.');
      return;
    }

    try {
      setLoading(true);

      if (mode === 'create') {
        await newReleasesApi.createNewReleasesList({
          month: formData.month
        });
      } else {
        await newReleasesApi.duplicateNewReleasesList({
          source_month: formData.source_month,
          target_month: formData.month
        });
      }

      // Pass the month back so the page can keep a freshly created (still empty)
      // month visible until its first book is added.
      onSuccess(formData.month, mode);
      onClose();
    } catch (err) {
      let errorMessage = err.message || 'Failed to create list';
      if (errorMessage.includes('already exists')) {
        errorMessage = 'A New Releases list already exists for this month.';
      } else if (errorMessage.includes('not found')) {
        errorMessage = 'Source list not found. Please verify the source month.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Generate recent months for dropdown
  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i - 3, 1);
      const monthValue = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ value: monthValue, label: monthLabel });
    }

    return options;
  };

  const monthOptions = generateMonthOptions();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-text-primary">
            Create New Releases List
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="text-red-800">{error}</span>
              </div>
            </div>
          )}

          {/* Mode Selection */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-3">
              Creation Method
            </label>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setMode('create')}
                className={`flex items-center space-x-2 px-4 py-2 border rounded-lg ${
                  mode === 'create'
                    ? 'border-primary-300 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Plus className="w-4 h-4" />
                <span>Create Empty</span>
              </button>
              <button
                type="button"
                onClick={() => setMode('duplicate')}
                className={`flex items-center space-x-2 px-4 py-2 border rounded-lg ${
                  mode === 'duplicate'
                    ? 'border-primary-300 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Copy className="w-4 h-4" />
                <span>Duplicate Existing</span>
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'duplicate' && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-text-primary">Source List</h4>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Source Month *
                  </label>
                  <select
                    value={formData.source_month}
                    onChange={(e) => handleInputChange('source_month', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
                    required
                  >
                    <option value="">Select source month...</option>
                    {monthOptions.map(month => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
              New Releases lists no longer own an age range. Each book uses its canonical reading age from the books table.
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Target Month *
              </label>
              <select
                value={formData.month}
                onChange={(e) => handleInputChange('month', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
                required
              >
                {monthOptions.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-6 py-2 border border-gray-200 text-text-secondary rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center space-x-2 disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  mode === 'create' ? <Plus className="w-4 h-4" /> : <Copy className="w-4 h-4" />
                )}
                <span>{loading ? 'Creating...' : (mode === 'create' ? 'Create List' : 'Duplicate List')}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateListModal;
